import React, { useEffect, useRef, useState } from 'react';
import jwtDecode from 'jwt-decode';
import { connect } from 'react-redux';

import { resfreshUserToken } from '../../services/authentication/refresh/actions';

import './SessionTimeoutWarning.scss';

const CHECK_INTERVAL_MS = 30 * 1000;
const WARNING_THRESHOLD_MS = 2 * 60 * 1000;

const getTimeBeforeExpiryMs = token => {
    try {
        const exp = jwtDecode(token).exp;
        return exp * 1000 - Date.now();
    } catch (e) {
        return -1;
    }
};

const formatRemaining = ms => {
    if (ms <= 0) return '0';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Avertit l'utilisateur de l'expiration imminente de sa session
 * et lui permet de la prolonger.
 */
const SessionTimeoutWarning = ({ token, isAuthenticated, refreshToken, i18n }) => {
    const [remaining, setRemaining] = useState(null);
    const buttonRef = useRef(null);
    const wasVisibleRef = useRef(false);

    useEffect(() => {
        if (!isAuthenticated || !token) {
            setRemaining(null);
            wasVisibleRef.current = false;
            return undefined;
        }

        const tick = () => {
            const ms = getTimeBeforeExpiryMs(token);
            setRemaining(ms <= WARNING_THRESHOLD_MS && ms > 0 ? ms : null);
        };

        tick();
        const interval = window.setInterval(tick, CHECK_INTERVAL_MS);
        return () => window.clearInterval(interval);
    }, [token, isAuthenticated]);

    useEffect(() => {
        const isVisible = remaining !== null;
        if (isVisible && !wasVisibleRef.current && buttonRef.current) {
            buttonRef.current.focus();
        }
        wasVisibleRef.current = isVisible;
    }, [remaining]);

    if (remaining === null || !i18n) {
        return null;
    }

    const handleExtend = () => {
        refreshToken();
        setRemaining(null);
    };

    return (
        <div
            role="alertdialog"
            aria-labelledby="session-timeout-title"
            aria-describedby="session-timeout-desc"
            aria-live="assertive"
            aria-atomic="true"
            className="session-timeout-warning">
            <div className="session-timeout-content">
                <h2 id="session-timeout-title">{i18n.TITLE}</h2>
                <p id="session-timeout-desc">{i18n.MESSAGE}</p>
                <p className="session-timeout-countdown" aria-hidden="true">
                    {i18n.TIME_REMAINING.replace('{0}', formatRemaining(remaining))}
                </p>
                <button
                    ref={buttonRef}
                    type="button"
                    className="session-timeout-extend"
                    onClick={handleExtend}>
                    {i18n.EXTEND}
                </button>
            </div>
        </div>
    );
};

const mapStateToProps = state => ({
    token: state.authentication.token,
    isAuthenticated: state.authentication.isAuthenticated,
    i18n: state.i18n.feature.SESSION_TIMEOUT
});

const mapDispatchToProps = {
    refreshToken: resfreshUserToken
};

export default connect(mapStateToProps, mapDispatchToProps)(SessionTimeoutWarning);



// WEBPACK FOOTER //
// ./src/components/rgaa/SessionTimeoutWarning.js